import React from "react"
import { graphql } from "gatsby"

import Seo from "../components/seo"

import RenderBlock from "../utils/render-block"

import useGlobalComponents from "../hooks/use-global-components"

const SingleProductSchema = ({ productData, siteUrl }) => {
  // get the siteUrl from the site metadata

  const machineBanner = productData.acf.contentBlocks.find(el => el.__typename === "WpMachine_Acf_ContentBlocks_MachineBanner");

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": productData.title,
    "image": siteUrl + machineBanner?.backgroundImage?.localFile?.childImageSharp?.gatsbyImageData.images.fallback.src,
    "description": productData.seo.metaDesc,
    "sku": productData.sku,
    "brand": {
      "@type": "Brand",
      "name": machineBanner?.brand?.length && machineBanner?.brand[0]?.title
    },
    "offers": {
      "@type": "Offer",
      //"price": productData.price,
      //"priceCurrency": '',
      "availability": `https://schema.org/InStock`,
      "url": siteUrl + productData.uri
    }
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </>
  );
};

const CatalogSchema = ({ catalogData, siteUrl }) => {

  let products = catalogData.acf.contentBlocks.find(el => el.__typename === "WpMachine_Acf_ContentBlocks_MachineArchive");
  if (products) {
    products = products.machineItems?.map(el => el.machine[0]).filter(el => el)?.map(
      el => ({
        name: el.title,
        image: siteUrl + el.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData.images.fallback.src,
        description: el.excerpt,
        //sku: el.sku,
        //brand: el.brand[0]?.title,
        //price: el.price,
        //currency: '',
        availability: 'InStock',
        url: siteUrl + el.uri
      })
    );
  }

  if (!products) {
    products = catalogData.acf.contentBlocks.filter(el => el.__typename === "WpMachine_Acf_ContentBlocks_InStockMachine")?.map(
      el => ({
        name: el.title,
        image: siteUrl + el.machineGallery[0]?.localFile?.childImageSharp?.gatsbyImageData.images.fallback.src,
        description: el.blurb,
        //sku: el.label,
        //brand: el.location,
        //price: el.shippingText,
        //currency: 'USD',
        availability: 'InStock',
        url: siteUrl + catalogData.uri
      })
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ProductCatalog",
    "name": catalogData.title,
    "description": catalogData.seo.metaDesc,
    "url": siteUrl + catalogData.uri,
    "publisher": {
      "@type": "Organization",
      "name": "Headland",
      "url": siteUrl,
    },
    "product": products?.map((product) => ({
      "@type": "Product",
      "name": product.name,
      "image": product.image,
      "description": product.description,
      "sku": product.sku,
      "brand": {
        "@type": "Brand",
        "name": product.brand
      },
      "offers": {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": product.currency,
        "availability": `https://schema.org/${product.availability}`,
        "url": product.url
      }
    }))
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </>
  );
};

const PageTemplate = ({ data, layoutData }) => {
  const { wpMachine } = data

  const globalComponents = useGlobalComponents()

  const globalComponetsId = wpMachine.acf.globalComponentsOverride?.databaseId || 35612
  const globalComponent = globalComponents.find(el => el.databaseId === globalComponetsId)

  const isCatalog = !wpMachine.acf.contentBlocks.find(el => el.__typename === "WpMachine_Acf_ContentBlocks_MachineBanner");

  return (
    <>
      <Seo
        bodyClass={`page-template dark-header page-template--${wpMachine.slug}`}
        {...wpMachine.seo}
      />
      { isCatalog ? 
        <CatalogSchema catalogData={wpMachine} siteUrl={layoutData.site.siteMetadata.siteUrl} /> : <SingleProductSchema productData={wpMachine} siteUrl={layoutData.site.siteMetadata.siteUrl} /> 
      }
      {wpMachine.acf.contentBlocks?.map((el, i) =>
        RenderBlock(wpMachine, el, i)
      )}
      {globalComponent?.acf?.contentBlocks?.map((el, i) => RenderBlock(wpMachine, el, i, 'globalComponent'))}
    </>
  )
}

export const pageQuery = graphql`
  query ($id: String!) {
    wpMachine(id: { eq: $id }) {
      title
      slug
      uri
      nodeType
      seo {
        ...WpSeo
      }
      ancestors {
        nodes {
          ... on WpMachine {
            title
            uri
          }
        }
      }
      wpChildren {
        nodes {
          ... on WpMachine {
            ...MachineCard
          }
        }
      }
      acf {
        globalComponentsOverride {
          ... on WpGlobalComponent {
            databaseId
          }
        }
        contentBlocks {
          __typename
          ... on WpMachine_Acf_ContentBlocks_Breadcrumbs {
            fieldGroupName
            alignment
          }
          ... on WpMachine_Acf_ContentBlocks_InteractiveBanner {
            subtitle
            title
            fieldGroupName
          }
          ... on WpMachine_Acf_ContentBlocks_PageBanner {
            fieldGroupName
            parallax
            height
            slides {
              content
              video
              title
              button {
                title
                url
              }
              image {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 2000)
                  }
                }
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_MachineBanner {
            fieldGroupName
            style
            title
            category
            description
            style
            button {
              url
              title
            }
            brand {
              ... on WpBrand {
                ...BrandCard
              }
            }
            backgroundImage {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 2000)
                }
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_Testimonials {
            fieldGroupName
            title
            testimonialSlider {
              ctaBoxDescription
              ctaBoxDescriptionSecond
              ctaBoxTitle
              ctaBoxTitleSecond
              fieldGroupName
              jobTitle
              name
              testimonial
              secondTextField
              link {
                title
                url
              }
              portraitImage {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 1000)
                  }
                }
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_Timeline {
            fieldGroupName
            title
          }
          ... on WpMachine_Acf_ContentBlocks_TeamArchive {
            fieldGroupName
            title
          }
          ... on WpMachine_Acf_ContentBlocks_ExpertsShowcase {
            fieldGroupName
            title
            items: experts {
              ... on WpTeam {
                ...TeamCard
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_BrandsShowcase {
            fieldGroupName
            disableLinking
            brands {
              ... on WpBrand {
                ...BrandCard
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_CategoryArchive {
            fieldGroupName
            content
            title
            categories {
              ... on WpMachine {
                ...MachineCard
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_BrandsArchive {
            fieldGroupName
            content
            title
            brands {
              ... on WpBrand {
                ...BrandCard
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_BrandIntro {
            fieldGroupName
            logo {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 600)
                }
              }
            }
            title
            content
          }
          ... on WpMachine_Acf_ContentBlocks_Gallery {
            fieldGroupName
            gallery {
              altText
              caption
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 1200)
                }
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_VideoBlock {
            fieldGroupName
            title
            video
          }
          ... on WpMachine_Acf_ContentBlocks_ResourcesBlock {
            fieldGroupName
            resources {
              ... on WpResource {
                ...ResourceCard
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_MachineArchive {
            fieldGroupName
            title
            content
            machines {
              ... on WpMachine {
                ...MachineCard
              }
            }
            machineItems {
              machine {
                ... on WpMachine {
                  ...MachineCard
                }
              }
              subMachines {
                title
                content
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_PostsArchive {
            fieldGroupName
            title
          }
          ... on WpMachine_Acf_ContentBlocks_RelatedTopics {
            fieldGroupName
            title
            items: posts {
              ... on WpResource {
                ...ResourceCard
              }
              ... on WpPost {
                ...ArticleCard
              }
              ... on WpMachine {
                ...MachineCard
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_LatestArticles {
            fieldGroupName
            style
            title
          }
          ... on WpMachine_Acf_ContentBlocks_IconBanner {
            fieldGroupName
            subtitle
            title
            backgroundImage {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 2000)
                }
              }
            }
            icon {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 1000)
                }
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_CategoryBanner {
            content
            fieldGroupName
            title
            button {
              target
              title
              url
            }
            backgroundImage {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 2000)
                }
              }
            }
          }

          ... on WpMachine_Acf_ContentBlocks_TabbedContent {
            fieldGroupName
            title
            style
            tabs {
              ... on WpMachine_Acf_ContentBlocks_TabbedContent_Tabs_CarouselContent {
                alignment
                content
                tabTitle
                fieldGroupName
                carousel {
                  altText
                  localFile {
                    childImageSharp {
                      gatsbyImageData(width: 1000, height: 750)
                    }
                  }
                }
              }
              ... on WpMachine_Acf_ContentBlocks_TabbedContent_Tabs_AccordianContent {
                alignment
                content
                tabTitle
                fieldGroupName
                accordian {
                  content
                  title
                }
              }
              ... on WpMachine_Acf_ContentBlocks_TabbedContent_Tabs_Video {
                fieldGroupName
                video
                tabTitle
              }
              ... on WpMachine_Acf_ContentBlocks_TabbedContent_Tabs_IconsGrid {
                fieldGroupName
                tabTitle
                items {
                  title
                  subtitle
                  icon {
                    altText
                    localFile {
                      childImageSharp {
                        gatsbyImageData(width: 1000)
                      }
                    }
                  }
                }
              }
              ... on WpMachine_Acf_ContentBlocks_TabbedContent_Tabs_ColumnContent {
                fieldGroupName
                tabTitle
                items {
                  content
                }
              }
              ... on WpMachine_Acf_ContentBlocks_TabbedContent_Tabs_ApplicationGallery {
                fieldGroupName
                tabTitle
                items {
                  fieldGroupName
                  imageTitle
                  blurb
                  image {
                    altText
                    localFile {
                      childImageSharp {
                        gatsbyImageData(width: 1000)
                      }
                    }
                  }
                }
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_ActiveCampaignForm {
            fieldGroupName
            content
            formId
          }
          ... on WpMachine_Acf_ContentBlocks_TabbedComponents {
            fieldGroupName
            title
            tabs {
              fieldGroupName
              tabTitle
              video
              leftColumn {
                fieldGroupName
                buttonText
                buttonUrl
                content
                title
              }
              rightColumn {
                fieldGroupName
                content
                title
                accordian {
                  content
                  title
                }
                gallery {
                  caption
                  localFile {
                    childImageSharp {
                      gatsbyImageData(width: 1000)
                    }
                  }
                }
              }
              iconList {
                fieldGroupName
                title
                subtitle
                icon {
                  id
                  localFile {
                    childImageSharp {
                      gatsbyImageData(width: 1000)
                    }
                  }
                }
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_InStockMachine {
            fieldGroupName
            title
            blurb
            bulletPoints {
              fieldGroupName
              bulletPointText
            }
            machineGallery {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 2000, height: 1400)
                }
              }
            }
            label
            location
            shippingText
            brochure {
              ... on WpResource {
                ...ResourceCard
              }
            }
            darkMode
            youtubeLink
            videoFile {
              link
              localFile {
                size
                extension
                publicURL
              }
            }
          }
          ... on WpMachine_Acf_ContentBlocks_ApplicationGallery {
            fieldGroupName
            title
            darkMode
            items {
              fieldGroupName
              imageTitle
              blurb
              image {
                altText
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 1000)
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

export default PageTemplate
